import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Box, makeStyles, Modal, Switch, Tooltip, Typography } from '@material-ui/core';
import Close from '@material-ui/icons/Close';
import Info from '@material-ui/icons/Info';
import useApi, { PUT_TABLE_BENCHMARKS } from 'hooks/useApi';
import { Label, ThemeButton } from 'components/shared';
import LoadingSpinner from 'components/LoadingSpinner';
import BenchmarkSearch from './BenchmarkSearch';

const useStyles = makeStyles(theme => ({
  root: {
    backgroundColor: theme.palette.white,
    position: 'absolute',
    width: '675px',
    height: '700px',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    outline: '0',
    borderRadius: '4px',
    padding: '16px',
    display: 'flex',
    flexDirection: 'column',
    userSelect: 'none'
  },
  container: {
    flexGrow: '1'
  },
  title: {
    fontSize: '0.9rem',
    marginBottom: '10px',
    fontWeight: theme.typography.fontWeightBold
  },
  section: {
    marginBottom: '15px',
    '&:last-child': {
      marginBottom: '0'
    }
  },
  searchToolbar: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center'
  },
  searchLabel: {
    display: 'flex',
    alignItems: 'center',
    '& svg': {
      color: theme.palette.primary.main,
      fontSize: '1.2rem',
      marginBottom: '10px',
      marginLeft: '4px'
    }
  },
  toggle: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: '10px'
  },
  toggleLabel: {
    fontSize: '0.8rem',
    color: theme.palette.grey.darker
  },
  benchmarkItem: {
    display: 'flex',
    color: theme.palette.grey.dark,
    padding: '5px 0',
    borderBottom: `1px solid ${theme.palette.grey.medium}`,
    justifyContent: 'space-between',
    '& p': {
      fontSize: '0.9rem'
    },
    '& svg': {
      cursor: 'pointer'
    }
  },
  loadingSpinner: {
    padding: '0'
  },
  uiError: {
    color: theme.palette.error.main,
    fontSize: '0.8rem'
  }
}));

const ActiveBenchmarksModal = ({
  id,
  masterObjectId,
  benchmarks,
  isOpen,
  onClose,
  setHasDataChanged
}) => {
  const [activeBenchmarks, setActiveBenchmarks] = useState(benchmarks);
  const [isAdvancedSearch, setIsAdvancedSearch] = useState(false);
  const [uiError, setUiError] = useState();
  const [state, sendRequest] = useApi();
  const { data: response, isLoading, isError } = state;
  const classes = useStyles();

  useEffect(() => {
    if (!response || isLoading || isError) return;
    if (response.success) {
      setHasDataChanged(true);
      onClose();
    }
  }, [isError, isLoading, onClose, response, setHasDataChanged]);

  const toggleAdvancedSearch = () => {
    setIsAdvancedSearch(!isAdvancedSearch);
  };

  const onItemAdded = benchmark => {
    if (uiError) {
      setUiError();
    }
    if (activeBenchmarks.some(ab => ab.timeSerieId === benchmark.timeSerieId)) {
      setUiError('This benchmark has already been added!');
      return;
    }
    if (activeBenchmarks.length >= 5) {
      setUiError('You can only compare 4 benchmarks at a time.');
      return;
    }

    const updatedBenchmarks = [...activeBenchmarks];
    updatedBenchmarks.push(benchmark);
    setActiveBenchmarks(updatedBenchmarks);
  };

  const onItemRemoved = benchmark => {
    if (uiError) {
      setUiError();
    }
    const index = activeBenchmarks.findIndex(b => b.timeSerieId === benchmark.timeSerieId);
    const updatedBenchmarks = [...activeBenchmarks];
    if (index > -1) {
      updatedBenchmarks.splice(index, 1);
      setActiveBenchmarks(updatedBenchmarks);
    }
  };

  const onSubmit = () => {
    const params = PUT_TABLE_BENCHMARKS({ id, data: activeBenchmarks });
    sendRequest(params);
  };

  const renderMasterObject = () => {
    const masterObject = activeBenchmarks.find(b => b.timeSerieId === masterObjectId);
    return (
      <Box className={classes.section}>
        <Typography className={classes.title}>Master Object</Typography>
        <Label text={masterObject.name} themeColor="primary" />
      </Box>
    );
  };

  const renderComparativeBenchmarks = () => {
    const comparativeBenchmarks = activeBenchmarks.filter(b => b.timeSerieId !== masterObjectId);
    return (
      <Box className={classes.section}>
        <Typography className={classes.title}>Compare Benchmarks</Typography>
        <Box>
          {comparativeBenchmarks.map(benchmark => (
            <Box key={benchmark.timeSerieId} className={classes.benchmarkItem}>
              <Typography>{benchmark.name}</Typography>
              <Close onClick={() => onItemRemoved(benchmark)} />
            </Box>
          ))}
        </Box>
        {uiError && <Typography className={classes.uiError}>{uiError}</Typography>}
      </Box>
    );
  };

  return (
    <Modal open={isOpen} onClose={onClose}>
      <Box className={classes.root}>
        <Box className={classes.container}>
          {renderMasterObject()}
          {renderComparativeBenchmarks()}
          <Box className={classes.section}>
            <Box className={classes.searchToolbar}>
              <Box className={classes.searchLabel}>
                <Typography className={classes.title}>Search</Typography>
                <Tooltip
                  placement="right-start"
                  title="Search results will be limited to 50 items. Please be specific!"
                >
                  <Info />
                </Tooltip>
              </Box>
              <Box className={classes.toggle}>
                <Switch
                  checked={isAdvancedSearch}
                  onChange={toggleAdvancedSearch}
                  size="small"
                  color="primary"
                />
                <Typography className={classes.toggleLabel}>Advanced</Typography>
              </Box>
            </Box>
            <BenchmarkSearch isAdvanced={isAdvancedSearch} onItemAdded={onItemAdded} />
          </Box>
        </Box>
        <Box className={classes.submit}>
          <ThemeButton
            themeColor="primary"
            style={{ width: '100%' }}
            onClick={onSubmit}
            disabled={isLoading}
          >
            {isLoading ? (
              <LoadingSpinner classes={{ root: classes.loadingSpinner }} size={18} />
            ) : (
              'Confirm Benchmarks'
            )}
          </ThemeButton>
        </Box>
      </Box>
    </Modal>
  );
};

ActiveBenchmarksModal.propTypes = {
  id: PropTypes.string.isRequired,
  masterObjectId: PropTypes.number.isRequired,
  benchmarks: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  isOpen: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  setHasDataChanged: PropTypes.func.isRequired
};

ActiveBenchmarksModal.defaultProps = {
  isOpen: false
};

export default ActiveBenchmarksModal;
